import React from 'react';
import {
  Link,
  withRouter,
  type WithRouterProps,
} from '@americanexpress/one-app-router';
import { useSelector } from 'react-redux';
import { getLocaleData } from '@/selectors/localeSelectors';
import {
  getBasePathFromLocation,
  cn,
  addTrailingSlashIfMissing,
} from '@/helpers/utils';
import { useAnalyticsContext } from '@/context/analyticsContext';
import type { ReactNode, MouseEvent } from 'react';

interface LFLinkProps {
  to: string;
  location: WithRouterProps['location'];
  searchParam?: string;
  pathname?: string;
  detail?: string;
  element?: string;
  className?: string;
  children?: ReactNode;
  onClickHandler?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

const LFLink: React.FC<LFLinkProps> = ({
  to: path,
  pathname,
  searchParam,
  element,
  location,
  className,
  detail = '',
  children,
  onClickHandler,
  ...restOfProps
}) => {
  const { locale }: { locale: string } = useSelector(getLocaleData);
  const {
    customTrack,
  }: {
    customTrack?: (args: {
      event: MouseEvent<HTMLAnchorElement>;
      detail: string;
      element: string;
    }) => void;
  } = useAnalyticsContext();
  const basePath = getBasePathFromLocation(locale);
  const queryParams = new URLSearchParams(location.search);
  const destination = path.startsWith('http') ? path : `${basePath}/${path}`;
  const resolvedPath = pathname || destination;
  const [pathWithoutHash, hashFragment] = resolvedPath.split('#');
  const hash = hashFragment ? `#${hashFragment}` : '';

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const elementValue = element ?? '';
    if (customTrack) {
      customTrack({
        event,
        detail,
        element: elementValue,
      });
    }
    if (onClickHandler) {
      onClickHandler(event);
    }
  };

  return (
    <Link
      className={cn('tw-no-underline', className)}
      to={{
        pathname: addTrailingSlashIfMissing(pathWithoutHash),
        search: searchParam ?? `?${queryParams}`,
        hash,
      }}
      onClick={handleClick}
      {...restOfProps}
    >
      {children}
    </Link>
  );
};

export default withRouter(LFLink);
